/** The second level: carve it (`underground.ts`), seat a far mouth under every
 * surface gate, furnish it from the underground tables, dress it, audit it
 * from the mouths, and reconcile the gates with the surface.
 *
 * Returns `null` when there is nothing worth a level - and takes the surface
 * mouths back out with it - so a one-level map never carries a gate to nowhere.
 */

import type { MapObjectState } from "@game/core";
import { assignZoneThemes } from "./pools.js";
import { zoneMap } from "./biomes.js";
import { SCENERY } from "./catalog.js";
import { GATE, channelOf, dropGates, isGate, reconcileGates } from "./gates.js";
import { chebyshev, idx, terrainOf, walkable, xOf, yOf } from "./grid.js";
import { decalScale, thicketScale } from "./landform.js";
import { placeNeutralTowns } from "./neutralTowns.js";
import type { PlaceContext } from "./place.js";
import {
  artifactPayload, dangerBand, dangerField, guardAt, nothing, place, placeGuard, poolOf,
  renumber, themed,
} from "./placement.js";
import { placeCaskets } from "./populate.js";
import { auditReachability } from "./audit.js";
import type { Rng } from "./rng.js";
import { chance, int, pick } from "./rng.js";
import type { GenState } from "./state.js";
import type { Decoration, GeneratedTown } from "./types.js";
import { carveUnderworld } from "./underground.js";

/** Below this many carved cells a cave is not worth a level of its own. */
const MIN_CAVE_FLOOR = 200;

interface Underground {
  readonly state: GenState;
  readonly objects: readonly MapObjectState[];
  readonly towns: readonly GeneratedTown[];
  readonly decorations: readonly Decoration[];
}

/** Underground danger starts at band 2. Nothing down there is a soft target. */
const caveBand = (u: GenState, i: number): number => Math.max(2, dangerBand(u, i));

/**
 * Carve the second level, put a far mouth under every surface gate that
 * survived the audit, fill it, and audit it from the mouths.
 *
 * Returns `null` - and takes the surface mouths back out with it - when there
 * is no usable cave or no pair could be completed. A level nobody can reach is
 * the largest isolated pocket there is.
 */
export function buildUnderworld(
  s: GenState, r: Rng, channels: readonly string[],
  monsterBp: number, decorationBp: number, sceneryBp: number,
): Underground | null {
  const width = s.g.width, height = s.g.height;
  const live = channels.filter((c) => s.objects.some((o) => isGate(o) && channelOf(o) === c));
  if (live.length === 0) return null;

  const cave = carveUnderworld(width, height, r);
  if (cave.floor.length < MIN_CAVE_FLOOR || cave.zones.length === 0) {
    for (const c of channels) dropGates(s, c);
    return null;
  }

  const zoneOf = zoneMap(cave.map, cave.zones);
  const ctx: PlaceContext = {
    map: cave.map, content: s.content, z: 1,
    reserved: new Uint8Array(width * height), anchors: [],
    scratch: new Uint8Array(width * height),
  };
  const u: GenState = {
    r, side: s.side, map: cave.map, g: s.g, content: s.content, ctx,
    objects: [], placements: [],
    danger: new Int32Array(width * height).fill(0x3fffffff),
    monsterBp, zoneOf, zones: cave.zones, idBase: 0,
  };

  // Each far mouth goes in the chamber nearest its partner, so a gate comes
  // out roughly under where it went in. The classic does the same and it is what
  // makes the two levels feel like one world rather than two maps.
  //
  // Note for whoever owns the kernel: `teleportTo` puts the arriving hero on
  // the destination object's *anchor*, and this def's anchor is a blocking
  // footprint cell rather than its entrance at (1, 0). The hero can still walk
  // out - `cellEnterable` is asked about the cell being entered, not the one
  // being left - but it arrives standing inside the gatehouse. Every portal
  // placed before this one was one cell wide, so the anchor was the entrance
  // and the bug could not show. The fix is one line in `teleportTo`.
  const mouths: number[] = [];
  for (const channel of live) {
    const surface = s.objects.find((o) => isGate(o) && channelOf(o) === channel);
    if (surface === undefined) continue;
    const from = idx(s.g, surface.x, surface.y);
    const near = [...cave.zones].sort((a, b) =>
      chebyshev(s.g, a.seed, from) - chebyshev(s.g, b.seed, from) || a.id - b.id);
    let linked = false;
    for (const zone of near.slice(0, 4)) {
      const cells = zone.cells.filter((i) => walkable(cave.map, i));
      const at = place(u, cells, GATE, () => ({ channel }), 25);
      if (at < 0) continue;
      const entrance = u.placements[at]?.entrances[0];
      if (entrance !== undefined) mouths.push(entrance);
      linked = true;
      break;
    }
    if (!linked) dropGates(s, channel);
  }
  if (mouths.length === 0) {
    for (const c of channels) dropGates(s, c);
    return null;
  }

  u.danger.set(dangerField(cave.map, s.g, mouths));

  // The town goes in before the cave is furnished, exactly as it does on the
  // surface. A town needs six by four of clear floor with a doorstep, which is
  // the largest clear block anything in this package asks for, and a chamber
  // that has already had its resource piles and its guards scattered through it
  // no longer has one: with the two calls the other way round the underground
  // got a town on one seed in twenty, which is not a feature, it is a fluke.
  const towns: GeneratedTown[] = [];
  placeNeutralTowns(u, 1, zoneOf, towns, ctx.reserved, 1, 60);
  populateUnderground(u);
  const decorations = decorateCave(u, decorationBp, sceneryBp);
  auditReachability(u, mouths);
  reconcileGates(s, u);

  if (u.objects.length === 0) return null;
  // Ids are positional across both levels: the underground's list is numbered
  // from where the surface's stops, and the surface list is final by now.
  u.idBase = s.objects.length;
  renumber(u);
  return { state: u, objects: u.objects, towns, decorations };
}

/**
 * What lives underground. The tables are deliberately not the surface's: this
 * is where the rare-resource mines are, where the banks pay, and where a guard
 * is a tier past what the same walk buys in daylight.
 */
function populateUnderground(u: GenState): void {
  assignZoneThemes(u.zones, u.content, [], u.r);
  const cat = poolOf(u);
  for (const zone of u.zones) {
    const cells = zone.cells.filter((i) => walkable(u.map, i));
    if (cells.length < 14) continue;
    const band = caveBand(u, zone.seed);

    for (let k = 0; k < 2 + int(u.r, 3); k++) {
      const defId = pick(u.r, cat.resources);
      if (defId === null) continue;
      const at = place(u, cells, defId, nothing, 12);
      if (at >= 0 && band >= 3) guardAt(u, at, band);
    }
    for (let k = 0; k < 1 + int(u.r, 2); k++) {
      const defId = pick(u.r, cat.chests);
      if (defId !== null) place(u, cells, defId, nothing, 12);
    }
    const mine = pick(u.r, cat.undergroundMines);
    if (mine !== null) guardAt(u, place(u, cells, mine, nothing, 25), band);
    // A bank in every far chamber, and in a third of the near ones. The
    // underground has to pay for the walk or nobody takes the stairs twice.
    if (band >= 3 || chance(u.r, 3500)) {
      const bank = themed(u, cat.undergroundBanks, zone);
      if (bank !== null) guardAt(u, place(u, cells, bank, nothing, 25), band + 1);
    }
    for (let k = 0; k < 1 + int(u.r, 2); k++) {
      const at = place(u, cells, "pu_artifact", () => artifactPayload(u, band), 15);
      if (at >= 0) guardAt(u, at, band + 1);
    }
    const dwelling = themed(u, cat.undergroundDwellings, zone);
    if (dwelling !== null) place(u, cells, dwelling, nothing, 25);
    // A box in every chamber that already earns a bank. Underground bands
    // start at 2, so this is always the guarded kind.
    if (band >= 3) placeCaskets(u, cells, band);
    place(u, cells, "bld_obelisk", nothing, 15);
    for (let k = 0; k < 2 + int(u.r, 3); k++) {
      const cell = cells[int(u.r, cells.length)];
      if (cell === undefined) continue;
      placeGuard(u, cell, band);
    }
  }
}

/**
 * Cave dressing. Same split as the surface: blocking scenery is a real object
 * and passes the same connectivity gate, decals never enter `GameState`. Rock
 * is skipped outright - a stalagmite inside solid stone is a sprite nobody can
 * ever see.
 */
function decorateCave(u: GenState, decorationBp: number, sceneryBp: number): Decoration[] {
  const out: Decoration[] = [];
  const m = u.map, g = u.g;
  const n = g.width * g.height;

  // Same landform fields as the surface, and for the same reason: a cave floor
  // scattered at one rate per cell is as flat a reading as a meadow is.
  for (let i = 0; i < n; i++) {
    if (!walkable(m, i) || m.occupant[i] !== 0 || u.ctx.reserved[i] === 1) continue;
    if (int(u.r, 10000) * 1000 >= sceneryBp * thicketScale(xOf(g, i), yOf(g, i))) continue;
    const defId = pick(u.r, SCENERY[terrainOf(m, i)]?.blocking ?? []);
    if (defId !== null) place(u, [i], defId, nothing, 1);
  }
  for (let i = 0; i < n; i++) {
    if (!walkable(m, i) || m.occupant[i] !== 0) continue;
    if (int(u.r, 10000) * 1000 >= decorationBp * decalScale(xOf(g, i), yOf(g, i))) continue;
    const defId = pick(u.r, SCENERY[terrainOf(m, i)]?.decals ?? []);
    if (defId !== null) out.push({ def: defId, x: xOf(g, i), y: yOf(g, i), z: 1 });
  }
  out.sort((a, b) => (a.y - b.y) || (a.x - b.x) || (a.def < b.def ? -1 : a.def > b.def ? 1 : 0));
  return out;
}
